import { useEffect } from "react";

import { Button, Grid, Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CheckBoxOutlined, CreateOutlined } from "@mui/icons-material";

import nameof from "ts-nameof.macro";

import { AdminRoutesConfig } from "../../../../RoutesConfig";
import LinearStepper from "../../../../../common/components/LinearStepper";
import Success from "../../../../shared/SuccessModal";
import CheckboxExtended from "../../../../../common/components/mui-extended/CheckboxExtended";
import SectionExtended from "../../../../../common/components/mui-extended/SectionExtended";
import TextFieldExtended from "../../../../../common/components/mui-extended/TextFieldExtended";
import { ErrorMessages, Rules } from "../../../../../common/validationRules";
import { useAxiosLoader } from "../../../../../common/hooks/useAxiosLoader";
import { Mode, Step, useInputForm } from "../../../../../common/hooks/useInputForm";
import investmentBrokerService from "../../../../../services/investmentBrokerService";
import { InvestmentBrokerInputDto } from "../../../../../interfaces/admin/InvestmentBroker";

export default function Input() {
  const loading = useAxiosLoader(false);

  const defaultModel: InvestmentBrokerInputDto = { isActive: true };

  const form = useInputForm<InvestmentBrokerInputDto>({
    defaultModel: defaultModel,
    addAsync: investmentBrokerService.addInvestmentBrokerAsync,
    updateAsync: investmentBrokerService.updateInvestmentBrokerAsync,
    validate: async (model: InvestmentBrokerInputDto) => {
      if (model.name) {
        await investmentBrokerService
          .getInvestmentBrokerIdByNameAsync(model.name)
          .then(({ data: investmentBrokerId }) => {
            if (investmentBrokerId && investmentBrokerId !== form.id) {
              form.setError(nameof(defaultModel.name), ErrorMessages.InvestmentBrokerNameExists);
            }
          });
      }

      if (model.secretKey) {
        await investmentBrokerService
          .getInvestmentBrokerIdBySecretKeyAsync(model.secretKey)
          .then(({ data: investmentBrokerId }) => {
            if (investmentBrokerId && investmentBrokerId !== form.id) {
              form.setError(nameof(defaultModel.secretKey), ErrorMessages.SecretKeyExists);
            }
          });
      }
    },
  });

  useEffect(() => {
    (async () => {
      if (form.mode === Mode.EDIT) {
        await investmentBrokerService.getInvestmentBrokerAsync(form.id).then(({ data: investmentBroker }) => {
          form.recreateForm(investmentBroker);
        });
      }
    })();
  }, []);

  const renderStepper = () => <LinearStepper step={form.step} mode={form.mode} />;

  const renderSuccess = () => (
    <Success routePath={AdminRoutesConfig.InvestmentBroker}>{`на инв. дружество ${form.getValues(
      nameof(defaultModel.name)
    )}`}</Success>
  );

  const renderForm = () => (
    <form onSubmit={form.onSubmit}>
      <Stack spacing={3}>
        <SectionExtended title="Данни за инв. дружество">
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Име"
                name={nameof(defaultModel.name)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Required}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextFieldExtended
                label="Пореден номер"
                name={nameof(defaultModel.order)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={{ ...Rules.Required, ...Rules.Number }}
              />
            </Grid>
            <Grid item xs={12} md={4} display="flex" alignItems="center">
              <CheckboxExtended
                label="Активен"
                name={nameof(defaultModel.isActive)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextFieldExtended
                label="Електронна поща"
                name={nameof(defaultModel.email)}
                form={form}
                disabled={form.step === Step.PREVIEW}
                rules={Rules.Email}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextFieldExtended
                label="Секретен ключ"
                name={nameof(defaultModel.secretKey)}
                form={form}
                disabled={form.step === Step.PREVIEW}
              />
            </Grid>
          </Grid>
        </SectionExtended>
        <Stack direction="row-reverse" justifyContent="space-between">
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingPosition="start"
            size="large"
            startIcon={<CheckBoxOutlined />}
            onClick={() => form.scrollToFirstError()}
            type="submit"
          >
            {form.step === Step.INPUT ? "Преглед" : "Впиши"}
          </LoadingButton>

          {form.step === Step.PREVIEW && (
            <Button size="large" startIcon={<CreateOutlined />} onClick={() => form.onEdit()}>
              Редактирай
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Инв. дружества</Typography>
      {renderStepper()}
      {form.step === Step.SUCCESS ? renderSuccess() : renderForm()}
    </Stack>
  );
}
